import {TileLayer, Layer, XYZ, OSM} from '../models'
import Axios from 'axios'
import {getMapService} from '../reducers/map'

// alfagis tile service (wms-server's tiles and the Stamen archive in
// s3://alfagis-tileserver, behind CloudFront): Bulgaria only.
const TILES = 'https://db5q4qutmc5gh.cloudfront.net'

class BasemapHelper {
  public ownMapsList = [] as any
  public pupMapsList = [] as any
  private tiffsLoaded = false
  private pupsLoaded = false
  public ownMapNames = new Map()
  public pupYear = new Map()
  public pupQuarter = new Map()
  public pupEkatte = new Map()
  public ownMapcenter = new Map()

  public getAlfagisBasemaps = () => ['alfagis-orthophoto', 'alfagis-hybrid', 'alfagis-terrain'] as any
  public getOsmBasemaps = () => ['osm', 'open-topo', 'etk', 'etk-new'] as any
  public getWindyBasemaps = () => ['windy-tourist'] as any
  public getStamenBasemaps = () => ['stamen-terrain', 'stamen-watercolor', 'stamen-toner', 'stamen-toner-light'] as any
  public getEmptyMap = () => ['empty'] as any
  public ownMaps = () => this.ownMapsList as any
  public pupMaps = () => this.pupMapsList as any
  public getAllBasemaps = () =>
    [
      ...this.getAlfagisBasemaps(),
      ...this.getOsmBasemaps(),
      ...this.getWindyBasemaps(),
      ...this.getStamenBasemaps(),
      ...this.getEmptyMap(),
      ...this.ownMaps()
    ] as any

  public getFree(): any {
    return [...this.getOsmBasemaps(), ...this.getStamenBasemaps()] as any
  }
  getNonComerical(): string[] {
    return [...this.getOsmBasemaps(), ...this.getStamenBasemaps()]
  }
  // Orthophoto (Ministry of Agriculture), orthophoto with streets, and a
  // terrain map (Ministry relief + OpenStreetMap), up to zoom 19; OpenLayers
  // enlarges zoom 19 beyond that.
  public getAlfagisBasemap(key: string): Layer {
    const layers: {[key: string]: [string, string]} = {
      'alfagis-orthophoto': ['Basemap_Orthophoto', 'Ортофото © МЗХ'],
      'alfagis-hybrid': [
        'Basemap_Hybrid',
        'Ортофото © МЗХ, улици © <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>'
      ],
      'alfagis-terrain': [
        'Basemap_Terrain',
        'Релеф © МЗХ, © <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>'
      ]
    }
    const [layer, attributions] = layers[key] || layers['alfagis-orthophoto']
    return new TileLayer({
      source: new XYZ({
        attributions,
        url: `${TILES}/tiles/${layer}/{z}/{x}/{y}.jpg`,
        maxZoom: 19,
        crossOrigin: 'Anonymous'
      })
    })
  }

  public getOsmBasemap(key: string): Layer {
    if (key == 'open-topo') {
      return new TileLayer({
        source: new XYZ({
          attributions:
            '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors' +
            ', ' +
            '<a href="https://opentopomap.org" target="_blank">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/" target="_blank">CC-BY-SA</a>)',
          url: 'https://a.tile.opentopomap.org/{z}/{x}/{y}.png',
          crossOrigin: 'Anonymous'
        })
      })
    }
    if (key == 'etk') {
      return new TileLayer({
        source: new XYZ({
          url: 'https://drone.alfagis.bg/etk/{z}/{x}/{y}.png',
          crossOrigin: 'Anonymous'
        })
      })
    }
    // Contour lines from the Ministry's elevation model (Relief:Contours of our
    // tile service), up to zoom 19.
    if (key == 'etk-new') {
      return new TileLayer({
        source: new XYZ({
          attributions: 'Хоризонтали © МЗХ',
          url: `${TILES}/tiles/Relief_Contours/{z}/{x}/{y}.png`,
          maxZoom: 19,
          crossOrigin: 'Anonymous'
        })
      })
    }
    if (key == 'drone') {
      return new TileLayer({
        source: new XYZ({
          url: 'https://drone.alfagis.bg/drone/{z}/{x}/{y}.png',
          crossOrigin: 'Anonymous'
        })
      })
    }
    return new TileLayer({
      source: new OSM()
    })
  }

  public getWindyBasemap(key: string): Layer {
    if (key == 'windy-tourist') {
      return new TileLayer({
        source: new XYZ({
          attributions:
            '&copy;<a href="https://www.seznam.cz/" target="_blank">Seznam.cz a.s.</a>' +
            ', ' +
            '&copy;<a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>',
          url: 'https://windytiles.mapy.cz/turist-m/{z}-{x}-{y}',
          crossOrigin: 'Anonymous'
        })
      })
    }
    return new TileLayer({
      source: new XYZ({
        attributions:
          '&copy;<a href="https://www.seznam.cz/" target="_blank">Seznam.cz a.s.</a>' +
          ', ' +
          '&copy;<a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>',
        url: 'https://windytiles.mapy.cz/turist-m/{z}-{x}-{y}',
        crossOrigin: 'Anonymous'
      })
    })
  }

  // Stamen's tiles from our archive of them (Stamen's own servers are gone),
  // up to zoom 18; OpenLayers enlarges zoom 18 beyond that.
  public getStamenBasemap(key: string): Layer {
    const tonerAtt =
      'Map tiles by <a href="http://stamen.com">Stamen Design</a>, under <a href="http://creativecommons.org/licenses/by/3.0">CC BY 3.0</a>. Data by <a href="http://openstreetmap.org">OpenStreetMap</a>, under <a href="http://www.openstreetmap.org/copyright">ODbL</a>.'
    const waterAtt =
      'Map tiles by <a href="http://stamen.com">Stamen Design</a>, under <a href="http://creativecommons.org/licenses/by/3.0">CC BY 3.0</a>. Data by <a href="http://openstreetmap.org">OpenStreetMap</a>, under <a href="http://creativecommons.org/licenses/by-sa/3.0">CC BY SA</a>.'
    const layers: {[key: string]: [string, string, string]} = {
      'stamen-terrain': ['terrain', 'png', tonerAtt],
      'stamen-toner': ['toner-background', 'png', tonerAtt],
      'stamen-toner-light': ['toner-lite', 'png', tonerAtt],
      'stamen-watercolor': ['watercolor', 'jpg', waterAtt]
    }
    const [layer, ext, attributions] = layers[key] || layers['stamen-terrain']
    return new TileLayer({
      source: new XYZ({
        attributions,
        url: `${TILES}/stamen/${layer}/{z}/{x}/{y}.${ext}`,
        maxZoom: 18,
        crossOrigin: 'Anonymous'
      })
    })
  }
  public getOwnBasemap(key: string): Layer {
    if (this.ownMapcenter.has(key) && this.ownMapcenter.get(key) != null) {
      const t = this.ownMapcenter.get(key).split(',')
      const center = [parseFloat(t[0]), parseFloat(t[1])]
      const minzoom = this.pupMapsList.indexOf(key) != -1 ? 20 : 18
      if (getMapService().getZoom() < minzoom) {
        getMapService().setCenter(center)
      }
      if (getMapService().getZoom() < 14) {
        getMapService().setZoom(14)
      }
    }

    return new TileLayer({
      source: new XYZ({
        url: 'https://drone.alfagis.bg/' + key + '/output/{z}/{x}/{y}.png',
        crossOrigin: 'Anonymous'
      })
    })
  }

  public async getOwnMap() {
    if (this.tiffsLoaded) return
    this.tiffsLoaded = true
    const response = await Axios.get(process.env.HOST_API_URL + '/GetTiffs')
    for (let i = 0; i < response.data.length; i++) {
      if (response.data[i].Status != 1) continue
      this.ownMapsList.push(response.data[i].path)
      this.ownMapNames.set(response.data[i].path, response.data[i].Name)
      this.ownMapcenter.set(response.data[i].path, response.data[i].center)
    }
  }

  public static GetPupType(type: number) {
    switch (type) {
      case 0:
        return 'ПУП - ПРЗ'
      case 1:
        return 'ПУП - ПР'
      case 2:
        return 'ПУП - ПУР'
      case 3:
        return 'ПУП - ПЗ'
      case 4:
        return 'ПУП - РУП'
      case 5:
        return 'ПУП'
    }
  }

  public async GetPupMaps() {
    if (this.pupsLoaded) return
    this.pupsLoaded = true
    const response = await Axios.get(process.env.HOST_API_URL + '/GetPups')
    for (let i = 0; i < response.data.length; i++) {
      if (response.data[i].Status != 1) continue
      const quarter = response.data[i].Quarter == 'null' ? '' : 'кв. ' + response.data[i].Quarter

      const path = response.data[i].path.split('/')[0]
      const name =
        response.data[i].EKATTE +
        ' - ' +
        quarter +
        ' - ' +
        BasemapHelper.GetPupType(response.data[i].PupType) +
        ' - ' +
        response.data[i].Year +
        ' г. ' +
        response.data[i].Description

      this.pupMapsList.push(path)
      this.pupYear.set(path, response.data[i].Year)
      this.pupQuarter.set(path, response.data[i].Quarter)
      this.pupEkatte.set(path, response.data[i].EKATTE)
      this.ownMapNames.set(path, name)
      this.ownMapcenter.set(path, response.data[i].center)
    }
  }
}

export const basemapHelper = new BasemapHelper()
